import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ERROR_DIALOG } from 'gcs-common/constants/errorDialogConstants';
import { toast } from 'gcs-common/design-system/hooks/use-toast';
import { useHandleQueryError } from 'gcs-common/helper/errorMessages';
import { useDispatch } from 'react-redux';
import type { Parameters, RequestBody } from '../../../../../types/typeHelper';
import fetchCurrentUserChannels from '../../../../slices/channels/channelsThunks/fetchCurrentUserChannels';
import { useApiClient } from '../../client';

export enum ChannelClientQueryKeys {
  INFO_PANEL_DETAILS = 'INFO_PANEL_DETAILS',
}

export function useStartTypingMutation() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async ({ params }: { params: Parameters<'/chat/channels/{channel_id}/start-typing', 'put'> }) => {
      return client.PUT('/chat/channels/{channel_id}/start-typing', {
        params,
      });
    },
  });
}

export function useInfoPanelDetails(channelId: string) {
  const client = useApiClient();
  const handleQueryError = useHandleQueryError();
  return useQuery({
    queryKey: [ChannelClientQueryKeys.INFO_PANEL_DETAILS, channelId],
    queryFn: async () => {
      const { data, error } = await client.GET('/chat/channels/{channel_id}/info-panel-details', {
        params: { path: { channel_id: channelId } },
      });
      handleQueryError({
        errorDialog: ERROR_DIALOG.NONE,
        // @ts-expect-error mismatch between openapi-fetch error and backend error types
        error,
      });

      return data;
    },
    enabled: !!channelId,
  });
}

export function useMarkChannelReadMutation() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async ({ channelId }: { channelId: string }) => {
      return client.POST('/chat/channels/{channel_id}/mark-read', {
        params: { path: { channel_id: channelId } },
      });
    },
    onError: (error) => {
      toast({
        description: error.message,
        duration: 5000,
        variant: 'destructive',
      });
    },
  });
}

export function useClearChannelMarkReadMutation() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async ({ channelId }: { channelId: string }) => {
      return client.DELETE('/chat/channels/{channel_id}/mark-read', {
        params: { path: { channel_id: channelId } },
      });
    },
    onError: (error) => {
      toast({
        description: error.message,
        duration: 5000,
        variant: 'destructive',
      });
    },
  });
}

export function useAddMembersToChannelMutation() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async ({
      body,
      path,
    }: {
      path: Parameters<'/chat/channels/{channel_id}/member', 'post'>['path'];
      body: RequestBody<'/chat/channels/{channel_id}/member', 'post'>;
    }) => {
      return client.POST('/chat/channels/{channel_id}/member', {
        params: { path },
        body,
      });
    },
    onError: (error) => {
      toast({
        description: error.message,
        duration: 5000,
        variant: 'destructive',
      });
    },
    onSettled: (_data, _error, variables) => {
      dispatch(fetchCurrentUserChannels({ specificChannelId: variables.path.channel_id }));
      queryClient.invalidateQueries({
        queryKey: [ChannelClientQueryKeys.INFO_PANEL_DETAILS, variables.path.channel_id],
      });
    },
  });
}

export function useRemoveMembersFromChannelMutation() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async ({
      path,
      body,
    }: {
      path: Parameters<'/chat/channels/{channel_id}/member', 'delete'>['path'];
      body: RequestBody<'/chat/channels/{channel_id}/member', 'delete'>;
    }) => {
      return client.DELETE('/chat/channels/{channel_id}/member', {
        params: { path },
        body,
      });
    },
    onError: (error) => {
      toast({
        description: error.message,
        duration: 5000,
        variant: 'destructive',
      });
    },
    onSettled: (_data, _error, variables) => {
      dispatch(fetchCurrentUserChannels({ specificChannelId: variables.path.channel_id }));
      queryClient.invalidateQueries({
        queryKey: [ChannelClientQueryKeys.INFO_PANEL_DETAILS, variables.path.channel_id],
      });
    },
  });
}

export function useUpdateChannelNameMutation() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async ({
      path,
      body,
    }: {
      path: Parameters<'/chat/channels/{channel_id}/name', 'post'>['path'];
      body: RequestBody<'/chat/channels/{channel_id}/name', 'post'>;
    }) => {
      return client.POST('/chat/channels/{channel_id}/name', {
        params: { path },
        body,
      });
    },
    onError: (error) => {
      toast({
        description: error.message,
        duration: 5000,
        variant: 'destructive',
      });
    },
    onSettled: (_data, _error, variables) => {
      dispatch(fetchCurrentUserChannels({ specificChannelId: variables.path.channel_id }));
      queryClient.invalidateQueries({
        queryKey: [ChannelClientQueryKeys.INFO_PANEL_DETAILS, variables.path.channel_id],
      });
    },
  });
}
